import { useCallback, useContext } from 'react';
import moment from 'moment';
import { compact } from 'lodash';
import { useRecoilValue } from 'recoil';
import { searchMetadata, type ActionEnum, type ComplianceExportRow } from 'venn-api';
import { onExportExcel, formatDate, UserContext } from 'venn-components';
import type { ExcelCell } from 'venn-utils';
import { useHasFF } from 'venn-utils';
import { displayZeroFeeState } from 'venn-state';
import { Notifications, NotificationType, type DateRange } from 'venn-ui-kit';

const SHEET_NAME = 'Report Metadata';

const toPercentCell = (value: number | null): ExcelCell => ({
  value: value ?? '',
  percentage: value !== null,
});

const toCurrencyCell = (value: number | null): ExcelCell => ({
  value: value ?? '',
  currency: value !== null,
});

const toTextCell = (value: string | null): ExcelCell => ({
  value: value ?? '',
});

const toBoolCell = (value: boolean): ExcelCell => ({
  value: value ? 'Yes' : 'No',
});

const taxBoolCell = (isInvestment: boolean, value: boolean): ExcelCell =>
  isInvestment ? { value: '' } : toBoolCell(value);

const nullableBoolCell = (isInvestment: boolean, value: boolean | null): ExcelCell =>
  isInvestment || value === null ? { value: '' } : toBoolCell(value);

const taxPercentCell = (isInvestment: boolean, value: number | null): ExcelCell =>
  toPercentCell(isInvestment ? null : value);

const taxCurrencyCell = (isInvestment: boolean, value: number | null): ExcelCell =>
  toCurrencyCell(isInvestment ? null : value);

const formatSubjectType = (subjectType: string | null): string => {
  switch (subjectType) {
    case 'PORTFOLIO':
      return 'Portfolio';
    case 'INVESTMENT':
      return 'Investment';
    case 'PRIVATE_PORTFOLIO':
      return 'Private Portfolio';
    case 'PRIVATE_FUND':
      return 'Private Investment';
    default:
      return subjectType ?? '';
  }
};

const toDateCell = (timestamp: string | null): ExcelCell => ({
  value: timestamp ? moment.utc(Math.round(Number(timestamp) * 1000)).format('M/D/YY') : '',
});

const buildRows = (
  rows: ComplianceExportRow[],
  includeOwnership: boolean,
  includeHouseholdClientColumns: boolean,
  includeTaxColumns: boolean,
  displayZeroFee: boolean,
  allowInvestmentFees: boolean,
): (ExcelCell | undefined)[][] => {
  const advisoryFeeFor = (row: ComplianceExportRow): number | null => {
    if (row.subjectType === 'INVESTMENT' && !allowInvestmentFees) return null;
    if (row.advisoryFee === 0 && !displayZeroFee) return null;
    return row.advisoryFee;
  };

  const headers: ExcelCell[] = compact([
    { value: 'Report Name', bold: true },
    { value: 'Subject', bold: true },
    { value: 'Subject Type', bold: true },
    includeHouseholdClientColumns ? { value: 'Household', bold: true } : undefined,
    includeOwnership ? { value: 'Ownership', bold: true } : undefined,
    includeHouseholdClientColumns ? { value: 'Client', bold: true } : undefined,
    { value: 'Advisory Fee', bold: true },
    { value: 'Has Investments Excluded from Fees', bold: true },
    includeTaxColumns ? { value: 'Has Taxable Investments', bold: true } : undefined,
    includeTaxColumns ? { value: 'Has Tax Deferred Investments', bold: true } : undefined,
    includeTaxColumns ? { value: 'Has Tax Exempt Investments', bold: true } : undefined,
    includeTaxColumns ? { value: 'Long Term Capital Gains Tax Rate', bold: true } : undefined,
    includeTaxColumns ? { value: 'Short Term Capital Gains Tax Rate', bold: true } : undefined,
    includeTaxColumns ? { value: 'Net Investment Income Tax Rate', bold: true } : undefined,
    includeTaxColumns ? { value: 'State Tax Rate', bold: true } : undefined,
    includeTaxColumns ? { value: 'Capital Loss Deduction Cap', bold: true } : undefined,
    { value: 'Saved By', bold: true },
    { value: 'Export Date', bold: true },
  ]);

  const dataRows = rows.map((row): (ExcelCell | undefined)[] => {
    const isInvestment = row.subjectType === 'INVESTMENT';
    return compact([
      toTextCell(row.reportName),
      toTextCell(row.subjectName),
      toTextCell(formatSubjectType(row.subjectType)),
      includeHouseholdClientColumns ? toTextCell(row.household) : undefined,
      includeOwnership ? toTextCell(row.ownership) : undefined,
      includeHouseholdClientColumns ? toTextCell(row.client) : undefined,
      toPercentCell(advisoryFeeFor(row)),
      nullableBoolCell(isInvestment, row.hasInvestmentsExcludedFromFees),
      includeTaxColumns ? taxBoolCell(isInvestment, row.hasTaxableInvestments) : undefined,
      includeTaxColumns ? taxBoolCell(isInvestment, row.hasTaxDeferredInvestments) : undefined,
      includeTaxColumns ? taxBoolCell(isInvestment, row.hasTaxExemptInvestments) : undefined,
      includeTaxColumns ? taxPercentCell(isInvestment, row.ltCapitalGainsRate) : undefined,
      includeTaxColumns ? taxPercentCell(isInvestment, row.stCapitalGainsRate) : undefined,
      includeTaxColumns ? taxPercentCell(isInvestment, row.niitRate) : undefined,
      includeTaxColumns ? taxPercentCell(isInvestment, row.stateTaxRate) : undefined,
      includeTaxColumns ? taxCurrencyCell(isInvestment, row.capitalLossDeductionCap) : undefined,
      toTextCell(row.savedBy),
      toDateCell(row.exportDate),
    ]);
  });

  return [headers, ...dataRows];
};

const buildFilename = (range: DateRange): string => {
  const from = formatDate(range.from) ?? '';
  const to = formatDate(range.to) ?? '';
  return `Report Metadata ${from} - ${to}`;
};

export const useComplianceMetadataExport = () => {
  const { hasPermission } = useContext(UserContext);
  const hasExportPermission = hasPermission('COMPLIANCE_EXPORT_READER' as ActionEnum);
  const canExport = useHasFF('compliance_metadata_export_ff');
  const includeOwnership = useHasFF('new_permissions_ff');
  const includeHouseholdClientColumns = useHasFF('rcm_strategy_enforcement_ff');
  const includeTaxColumns = useHasFF('tax_impact_ff');
  const allowInvestmentFees = useHasFF('advisory_fees_for_rl_investments_ff');
  const displayZeroFee = useRecoilValue(displayZeroFeeState);

  const exportMetadata = useCallback(
    async (range: DateRange): Promise<void> => {
      if (!canExport || !hasExportPermission || range.from === undefined || range.to === undefined) {
        return;
      }

      const toastId = Notifications.notify('Exporting metadata…', NotificationType.LOADING);
      const filename = buildFilename(range);

      try {
        const result = await searchMetadata({
          startDate: moment.utc(range.from).startOf('day').toISOString(),
          endDate: moment.utc(range.to).endOf('day').toISOString(),
        });

        const sheetData = buildRows(
          result.content,
          includeOwnership,
          includeHouseholdClientColumns,
          includeTaxColumns,
          displayZeroFee,
          allowInvestmentFees,
        );
        await onExportExcel(sheetData, filename, SHEET_NAME, true);

        Notifications.notifyUpdate(toastId, `${filename}.xlsx successfully downloaded`, NotificationType.SUCCESS);
      } catch {
        Notifications.notifyUpdate(toastId, 'Metadata download failed, please try again', NotificationType.ERROR);
      }
    },
    [
      canExport,
      hasExportPermission,
      includeOwnership,
      includeHouseholdClientColumns,
      includeTaxColumns,
      allowInvestmentFees,
      displayZeroFee,
    ],
  );

  return { canExport, hasExportPermission, exportMetadata };
};
